/**
 * API Client
 * Configured axios instance with interceptors for authentication and token refresh
 */

import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from "axios"
import { toast } from "sonner"
import { getNormalizedTimezone } from "../utils/timezone"
import { refreshAccessToken } from "./auth-refresh"
import { clearTokens, getAccessToken, getRefreshToken } from "./token-manager"

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
})

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

// Process queue after refresh
const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  failedQueue = []
}

/**
 * Request Interceptor
 * Adds access token to all requests
 * Automatically detects and adds user timezone
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Auto-detect user timezone and add to headers (normalized for deprecated names)
    const userTimezone = getNormalizedTimezone()
    if (userTimezone) {
      config.headers["x-timezone"] = userTimezone
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  },
)

/**
 * Response Interceptor
 * Handles token refresh on 401 errors and rate limiting (429) redirects
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    // Google Calendar connection required — show toast + redirect to settings
    const responseData = error.response?.data as { message?: string } | undefined
    if (
      error.response?.status === 400 &&
      responseData?.message?.toLowerCase().includes("connect your google calendar")
    ) {
      toast.error("Connect Google Calendar from settings to continue", {
        duration: 5000,
      })
      ;(error as unknown as Record<string, unknown>)._handled = true
      return Promise.reject(error)
    }

    // Handle rate limiting (429) — toast for interactive endpoints, redirect
    // only for auth/global endpoints where the user has no way to retry inline.
    if (error.response?.status === 429) {
      const retryAfter = Number(error.response.headers["retry-after"]) || 60
      const url = originalRequest.url ?? ""
      const isAuthEndpoint = url.startsWith("/auth")
      if (isAuthEndpoint) {
        window.location.href = `/error/too-many-requests?retry=${retryAfter}`
      } else {
        toast.error("Too many requests. Please wait a moment and try again.")
      }
      return Promise.reject(error)
    }

    // Any 403 on an authenticated endpoint means the account is blocked
    // (suspended, banned, or deactivated by admin). Login uses 401 so it
    // never hits this branch — the login component handles those directly.
    if (error.response?.status === 403) {
      clearTokens()
      window.location.href = "/login"
      return Promise.reject(error)
    }

    // Handle 404 — resource not found. Redirect to branded 404 page.
    if (error.response?.status === 404) {
      window.location.href = "/not-found"
      return Promise.reject(error)
    }

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    // If no refresh token, just pass error through
    // This means user is not logged in yet (e.g., wrong credentials on login screen)
    // Don't redirect - let the component handle the error
    if (!getRefreshToken()) {
      return Promise.reject(error)
    }

    // If already refreshing, add to queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then(token => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`
          }
          return apiClient(originalRequest)
        })
        .catch(err => {
          return Promise.reject(err)
        })
    }

    // Mark as refreshing
    isRefreshing = true
    originalRequest._retry = true

    try {
      // Shared refresh (deduped with the realtime client) so the rotating
      // refresh token is never double-spent. See lib/auth-refresh.ts.
      const accessToken = await refreshAccessToken()

      // Process queue with new token
      processQueue(null, accessToken)

      // Update original request and retry
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`
      }

      return apiClient(originalRequest)
    } catch (refreshError) {
      // Refresh failed, clear tokens and redirect to login
      processQueue(refreshError as Error, null)
      clearTokens()
      window.location.href = "/login"
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)

export default apiClient
